import React from 'react';
import PropTypes from 'prop-types';
import { Truck, ArrowRight } from 'lucide-react';
import Button from '../../Button';
import Price from '../../Price';
import Discount from '../Discount';
import './Summary.css';

/**
 * Shopping Cart Summary Component
 *
 * Displays order summary: delivery info, line items, discount rows,
 * total price, CTA button, promo text, and discount code input.
 *
 * Figma node: 3959:1595; `variant="panel"` = the OVER/LL cart (OVER-LL 2762:26499)
 *
 * Only one discount code can be applied at a time. Different codes
 * produce different discount labels and amounts.
 */
export default function Summary({
  // Title
  title = 'Summary',

  // Delivery
  showDelivery = true,
  deliveryTime = '3 Weeks',
  deliveryCost = 'Free',
  deliveryDescription = 'Delivery to your school',
  deliveryPrice = '0:-',

  // Line items (dynamic rows: products, packages, etc.)
  lineItems = [],
  // Legacy single-row fallback
  productsLabel = 'Products',
  productsPrice,

  // Discount rows (green, in details section)
  discounts = [],

  // Total
  totalLabel = 'Total',
  totalPrice,
  originalPrice = '',

  // CTA
  ctaLabel = 'Go to checkout',
  onCtaClick,
  ctaLoading = false,
  ctaDisabled = false,
  ctaSize = 'primary',
  ctaVariant = 'alternative',
  ctaClassName = '',

  // Promo
  promoTitle = 'BUY RISK FREE',
  promoSubtitle = '24 HOURS TO EDIT OR CANCEL YOUR ORDER',
  showPromo = true,

  // Discount code footer (single code)
  showDiscountCode = true,
  discountCodeLabel = 'Discount code',
  discountCode = '',          // applied code string
  discountSubmitted = false,  // is code applied?
  discountLabel = '',         // success message (e.g. '15% DISCOUNT')
  discountErrorCode = '',     // pre-fill input with invalid code
  discountErrorMessage = '',  // red error message
  onDiscountSubmit,
  onDiscountRemove,

  // Skin: 'card' (bordered card) | 'panel' (flat — the page column is the surface)
  variant = 'card',

  className = '',
}) {
  const hasDiscounts = discounts.length > 0;

  return (
    <div className={`sc-summary${variant === 'panel' ? ' sc-summary--panel' : ''}${className ? ` ${className}` : ''}`}>
      {/* ── Content section ── */}
      <div className="sc-summary__content">
        {/* Title */}
        <h2 className="sc-summary__title typo-h2">{title}</h2>

        {/* Details */}
        <div className="sc-summary__details">
          {/* Delivery row */}
          {showDelivery && (
          <div className="sc-summary__row sc-summary__row--delivery">
            <div className="sc-summary__delivery-info">
              <div className="sc-summary__delivery-icon">
                <Truck size={24} />
              </div>
              <div className="sc-summary__delivery-copy">
                <div className="sc-summary__delivery-meta">
                  <span className="typo-h4">{deliveryTime}</span>
                  <span className="sc-summary__dot typo-body-lg">•</span>
                  <span className="typo-h4">{deliveryCost}</span>
                </div>
                <span className="sc-summary__delivery-desc typo-body-sm">
                  {deliveryDescription}
                </span>
              </div>
            </div>
            <span className="sc-summary__row-price typo-body-lg">{deliveryPrice}</span>
          </div>
          )}

          {/* Line items (products, packages, etc.) */}
          {(lineItems.length > 0 ? lineItems : [{ label: productsLabel, price: productsPrice }]).map((item, i) => (
            <div key={i} className="sc-summary__row sc-summary__row--bordered">
              <span className="sc-summary__row-label typo-h4">{item.label}</span>
              <span className="sc-summary__row-price typo-body-lg">{item.price}</span>
            </div>
          ))}

          {/* Discount rows — only when discounts are applied */}
          {discounts.map((d, i) => (
            <div key={i} className="sc-summary__row sc-summary__row--bordered sc-summary__row--discount">
              <span className="sc-summary__row-label typo-h4">{d.label}</span>
              <span className="sc-summary__row-price typo-body-lg">{d.amount}</span>
            </div>
          ))}

          {/* Total row */}
          <div className="sc-summary__total">
            <span className="sc-summary__total-label typo-h3">{totalLabel}</span>
            <div className="sc-summary__total-prices">
              {hasDiscounts && originalPrice ? (
                <Price
                  type="medium"
                  state="discounted"
                  price={totalPrice}
                  originalPrice={originalPrice}
                />
              ) : (
                <Price
                  type="large"
                  state="default"
                  price={totalPrice}
                />
              )}
            </div>
          </div>

          {/* CTA Button */}
          <Button
            size={ctaSize}
            variant={ctaVariant}
            iconRight={<ArrowRight size={20} />}
            onClick={onCtaClick}
            loading={ctaLoading}
            disabled={ctaDisabled}
            className={`sc-summary__cta-btn${ctaClassName ? ` ${ctaClassName}` : ''}`}
          >
            {ctaLabel}
          </Button>

          {/* Promo */}
          {showPromo && (
            <div className="sc-summary__promo">
              <p className="sc-summary__promo-title typo-h4">{promoTitle}</p>
              <p className="sc-summary__promo-subtitle typo-body-sm">{promoSubtitle}</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Discount code section ── */}
      {showDiscountCode && (
        <div className="sc-summary__discount-section">
          <p className="sc-summary__discount-title typo-h4">{discountCodeLabel}</p>
          {discountSubmitted && discountCode ? (
            <Discount
              label={discountCodeLabel}
              code={discountCode}
              submitted
              discountLabel={discountLabel}
              onRemove={onDiscountRemove}
            />
          ) : (
            <Discount
              label={discountCodeLabel}
              code={discountErrorCode}
              error={!!discountErrorCode}
              errorLabel={discountErrorMessage}
              onSubmit={onDiscountSubmit}
            />
          )}
        </div>
      )}
    </div>
  );
}

Summary.propTypes = {
  title: PropTypes.string,
  deliveryTime: PropTypes.string,
  deliveryCost: PropTypes.string,
  deliveryDescription: PropTypes.string,
  deliveryPrice: PropTypes.string,
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      price: PropTypes.string.isRequired,
    })
  ),
  productsLabel: PropTypes.string,
  productsPrice: PropTypes.string,
  discounts: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      amount: PropTypes.string.isRequired,
    })
  ),
  totalLabel: PropTypes.string,
  totalPrice: PropTypes.string.isRequired,
  originalPrice: PropTypes.string,
  ctaLabel: PropTypes.string,
  onCtaClick: PropTypes.func,
  ctaLoading: PropTypes.bool,
  ctaDisabled: PropTypes.bool,
  /** Button size of the CTA */
  ctaSize: PropTypes.string,
  /** Button variant of the CTA */
  ctaVariant: PropTypes.string,
  /** Extra class on the CTA (placement hooks; a skin is a `ctaVariant`, e.g. `hero`) */
  ctaClassName: PropTypes.string,
  promoTitle: PropTypes.string,
  promoSubtitle: PropTypes.string,
  showPromo: PropTypes.bool,
  showDiscountCode: PropTypes.bool,
  discountCodeLabel: PropTypes.string,
  discountCode: PropTypes.string,
  discountSubmitted: PropTypes.bool,
  discountLabel: PropTypes.string,
  discountErrorCode: PropTypes.string,
  discountErrorMessage: PropTypes.string,
  onDiscountSubmit: PropTypes.func,
  onDiscountRemove: PropTypes.func,
  /** `card` = bordered card with the discount code as its footer; `panel` = flat, no surface of its own (the page column carries it), the discount code in its own card (OVER-LL cart 2762:26499) */
  variant: PropTypes.oneOf(['card', 'panel']),
  className: PropTypes.string,
};
